import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const CLICK_ZOOM_SCALE = 2.5;
// Abaixo desse deslocamento (px) o gesto conta como clique, não como pan.
const CLICK_MOVE_TOLERANCE = 6;

interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: Transform = { scale: 1, tx: 0, ty: 0 };

export interface ZoomableImageProps {
  src: string;
  alt?: string;
}

/**
 * Imagem com zoom dentro de um modal: roda do mouse / pinça do trackpad e do
 * touch ampliam em torno do cursor, arrastar faz pan quando ampliada, clique
 * alterna ajustar ↔ ampliar no ponto clicado. Botões +/−/reset no canto para
 * o gesto ser descobrível. O elemento pai define a área útil (o viewport
 * ocupa 100% de largura/altura e recorta o excesso).
 */
function ZoomableImage({ src, alt }: ZoomableImageProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const transformRef = useRef<Transform>(transform);
  transformRef.current = transform;

  // Ponteiros ativos (mouse/touch unificados) + estado do gesto em andamento.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureRef = useRef<{
    startTransform: Transform;
    startPoints: { x: number; y: number }[];
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    setTransform(IDENTITY);
  }, [src]);

  // Centro de layout da imagem (sem translate) e limite de pan: a imagem
  // ampliada pode ser arrastada até suas bordas alcançarem as do viewport,
  // nunca além (não há como "perder" a imagem fora da tela).
  const clampTransform = useCallback((t: Transform): Transform => {
    const img = imgRef.current;
    const viewport = viewportRef.current;
    if (!img || !viewport) return t;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, t.scale));
    if (scale === 1) return IDENTITY;
    const rect = img.getBoundingClientRect();
    const current = transformRef.current;
    const baseW = rect.width / current.scale;
    const baseH = rect.height / current.scale;
    const vp = viewport.getBoundingClientRect();
    const maxTx = Math.max(0, (baseW * scale - vp.width) / 2);
    const maxTy = Math.max(0, (baseH * scale - vp.height) / 2);
    return {
      scale,
      tx: Math.min(maxTx, Math.max(-maxTx, t.tx)),
      ty: Math.min(maxTy, Math.max(-maxTy, t.ty)),
    };
  }, []);

  // Amplia mantendo o ponto da imagem sob (mx,my) parado na tela.
  const zoomAt = useCallback((mx: number, my: number, newScale: number) => {
    const img = imgRef.current;
    if (!img) return;
    const { scale, tx, ty } = transformRef.current;
    const rect = img.getBoundingClientRect();
    const cx = rect.left + rect.width / 2 - tx;
    const cy = rect.top + rect.height / 2 - ty;
    const ratio = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale)) / scale;
    setTransform((prev) => clampTransform({
      scale: prev.scale * ratio,
      tx: mx - cx - (mx - cx - prev.tx) * ratio,
      ty: my - cy - (my - cy - prev.ty) * ratio,
    }));
  }, [clampTransform]);

  const zoomAtCenter = useCallback((factor: number) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const vp = viewport.getBoundingClientRect();
    zoomAt(vp.left + vp.width / 2, vp.top + vp.height / 2, transformRef.current.scale * factor);
  }, [zoomAt]);

  // Listener nativo não-passivo: preventDefault na roda para o zoom não
  // rolar a página/modal atrás (o onWheel sintético pode ser passivo).
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Pinça de trackpad chega como wheel+ctrlKey com deltas menores.
      const intensity = e.ctrlKey ? 0.01 : 0.0022;
      zoomAt(e.clientX, e.clientY, transformRef.current.scale * Math.exp(-e.deltaY * intensity));
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const handlePointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gestureRef.current = {
      startTransform: transformRef.current,
      startPoints: Array.from(pointersRef.current.values()),
      moved: gestureRef.current?.moved ?? false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const pointers = pointersRef.current;
    if (!pointers.has(e.pointerId) || !gestureRef.current) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const gesture = gestureRef.current;
    const points = Array.from(pointers.values());

    if (points.length === 2 && gesture.startPoints.length === 2) {
      // Pinça: escala pela razão das distâncias, ancorada no ponto médio.
      const dist = (p: { x: number; y: number }[]) => Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      const startDist = dist(gesture.startPoints) || 1;
      const midX = (points[0].x + points[1].x) / 2;
      const midY = (points[0].y + points[1].y) / 2;
      gesture.moved = true;
      zoomAt(midX, midY, gesture.startTransform.scale * (dist(points) / startDist));
      return;
    }

    if (points.length === 1 && gesture.startPoints.length >= 1) {
      const dx = points[0].x - gesture.startPoints[0].x;
      const dy = points[0].y - gesture.startPoints[0].y;
      if (Math.hypot(dx, dy) > CLICK_MOVE_TOLERANCE) gesture.moved = true;
      if (gesture.moved && transformRef.current.scale > 1) {
        setTransform(clampTransform({
          scale: gesture.startTransform.scale,
          tx: gesture.startTransform.tx + dx,
          ty: gesture.startTransform.ty + dy,
        }));
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const pointers = pointersRef.current;
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    const gesture = gestureRef.current;
    if (pointers.size > 0) {
      // Sobrou um dedo da pinça: reancora o gesto para virar pan contínuo.
      gestureRef.current = {
        startTransform: transformRef.current,
        startPoints: Array.from(pointers.values()),
        moved: true,
      };
      return;
    }
    if (gesture && !gesture.moved && e.pointerType !== 'touch') {
      // Clique simples (sem arrasto): alterna ajustar ↔ ampliar no ponto.
      if (transformRef.current.scale === 1) {
        zoomAt(e.clientX, e.clientY, CLICK_ZOOM_SCALE);
      } else {
        setTransform(IDENTITY);
      }
    }
    gestureRef.current = null;
  };

  const { scale, tx, ty } = transform;
  return (
    <Viewport
      ref={viewportRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      $zoomed={scale > 1}
    >
      <Img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
      />
      <Controls onPointerDown={(e) => e.stopPropagation()}>
        <ControlButton onClick={() => zoomAtCenter(1 / 1.5)} aria-label="Reduzir zoom" disabled={scale <= MIN_SCALE}>
          <ZoomOutIcon style={{ fontSize: 18 }} />
        </ControlButton>
        <ScaleLabel>{Math.round(scale * 100)}%</ScaleLabel>
        <ControlButton onClick={() => zoomAtCenter(1.5)} aria-label="Ampliar zoom" disabled={scale >= MAX_SCALE}>
          <ZoomInIcon style={{ fontSize: 18 }} />
        </ControlButton>
        {/* Sempre renderizado (desabilitado em 100%): se aparecesse só quando
            ampliado, a pílula cresceria e o botão + mudaria de lugar debaixo do
            cursor — o clique seguinte acertaria o reset (visto no E2E). */}
        <ControlButton onClick={() => setTransform(IDENTITY)} aria-label="Restaurar zoom" disabled={scale <= 1}>
          <RestartAltIcon style={{ fontSize: 18 }} />
        </ControlButton>
      </Controls>
    </Viewport>
  );
}

export default ZoomableImage;

const Viewport = styled.div<{ $zoomed: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  cursor: ${({ $zoomed }) => ($zoomed ? 'grab' : 'zoom-in')};

  &:active {
    cursor: ${({ $zoomed }) => ($zoomed ? 'grabbing' : 'zoom-in')};
  }
`;

const Img = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  border-radius: 8px;
  box-shadow: 0 2px 12px ${({ theme }) => theme.preview_shadow};
`;

const Controls = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 999px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(20, 20, 20, 0.75)' : 'rgba(255, 255, 255, 0.85)')};
  color: ${({ theme }) => theme.font_color};
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
  cursor: default;
`;

const ControlButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.hover_overlay_light};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const ScaleLabel = styled.span`
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  min-width: 38px;
  text-align: center;
  opacity: 0.85;
`;
